import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'

// Manages a list of in-flight uploads, each with its own progress + server
// status. Uses XHR (rather than fetch) so we can read upload progress events.
// After creation, polls GET /api/v1/artifacts/:id every few seconds until
// processing_status becomes "completed" or "failed".
export function useArtifactUploads() {
  const [uploads, setUploads] = useState([])
  const timersRef = useRef(new Map())
  // Dismissed uploads must stop polling even if a tick is mid-await when
  // dismiss() runs — clearing the timer alone can't catch that tick's
  // self-reschedule, which previously kept polling invisibly forever.
  const dismissedRef = useRef(new Set())

  const updateUpload = useCallback((localId, patch) => {
    setUploads((prev) =>
      prev.map((u) => (u.localId === localId ? { ...u, ...patch } : u))
    )
  }, [])

  const pollArtifact = useCallback(
    (localId, artifactId) => {
      const tick = async () => {
        if (dismissedRef.current.has(localId)) return
        try {
          const fresh = await api.get(`/api/v1/artifacts/${artifactId}`)
          updateUpload(localId, { artifact: fresh, status: fresh.processing_status })

          if (fresh.processing_status === 'completed' || fresh.processing_status === 'failed') {
            const timer = timersRef.current.get(localId)
            if (timer) clearTimeout(timer)
            timersRef.current.delete(localId)
            return
          }
        } catch (err) {
          console.warn('Polling failed', err)
        }
        if (dismissedRef.current.has(localId)) return // dismissed mid-await
        const next = setTimeout(tick, 2000)
        timersRef.current.set(localId, next)
      }
      const timer = setTimeout(tick, 1500)
      timersRef.current.set(localId, timer)
    },
    [updateUpload]
  )

  const startUpload = useCallback(
    (file, { initiativeId }) => {
      const localId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      setUploads((prev) => [
        ...prev,
        {
          localId,
          fileName: file.name,
          fileSize: file.size,
          progress: 0,
          status: 'uploading',
          error: null,
          artifact: null,
        },
      ])

      const xhr = new XMLHttpRequest()
      const formData = new FormData()
      formData.append('initiative_id', String(initiativeId))
      formData.append('file', file)
      formData.append('name', file.name)

      xhr.open('POST', '/api/v1/artifacts')
      xhr.withCredentials = true // send the Devise session cookie
      xhr.setRequestHeader('Accept', 'application/json')
      // Auth is the session cookie (JWT removed in Phase 1.5). This is a
      // state-changing POST, so it needs the CSRF token Rails expects.
      const csrf = typeof document !== 'undefined'
        ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        : null
      if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf)

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const pct = Math.round((event.loaded / event.total) * 100)
          updateUpload(localId, { progress: pct })
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let artifact = null
          try {
            artifact = JSON.parse(xhr.responseText)
          } catch {
            // fall through
          }
          updateUpload(localId, {
            progress: 100,
            status: artifact?.processing_status || 'pending',
            artifact,
          })
          if (artifact?.id) pollArtifact(localId, artifact.id)
        } else {
          let message = `Upload failed (${xhr.status})`
          try {
            const body = JSON.parse(xhr.responseText)
            if (body.error) message = body.error
          } catch {
            // ignore
          }
          updateUpload(localId, { status: 'failed', error: message })
        }
      }

      xhr.onerror = () => {
        updateUpload(localId, { status: 'failed', error: 'Network error' })
      }

      xhr.send(formData)
    },
    [pollArtifact, updateUpload]
  )

  const dismiss = useCallback((localId) => {
    dismissedRef.current.add(localId)
    setUploads((prev) => prev.filter((u) => u.localId !== localId))
    const timer = timersRef.current.get(localId)
    if (timer) clearTimeout(timer)
    timersRef.current.delete(localId)
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    const dismissed = dismissedRef.current
    return () => {
      timers.forEach((timer, localId) => {
        clearTimeout(timer)
        // Also flag as dismissed so a tick that's mid-await at unmount
        // doesn't reschedule itself afterwards.
        dismissed.add(localId)
      })
      timers.clear()
    }
  }, [])

  return { uploads, startUpload, dismiss }
}
